import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Activity, Heart, Thermometer, Droplets, User, Calendar, Search, Bell } from 'lucide-react';

const data = [
  { name: 'Mon', heartRate: 72, temp: 36.6 },
  { name: 'Tue', heartRate: 85, temp: 37.2 },
  { name: 'Wed', heartRate: 78, temp: 36.8 },
  { name: 'Thu', heartRate: 90, temp: 38.1 },
  { name: 'Fri', heartRate: 75, temp: 36.9 },
  { name: 'Sat', heartRate: 70, temp: 36.5 },
];

const EmployeeDashboard = () => {
  return (
    <div className="min-h-screen bg-slate-50 flex">
     
      {/* Main Content */}
      <main className="flex-1 p-8">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Employee Profile</h2>
            <p className="text-slate-500 text-sm">Case ID: #12903-PT</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input type="text" placeholder="Search records..." className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button className="p-2 bg-white border border-slate-200 rounded-full hover:bg-slate-50 relative">
              <Bell className="h-5 w-5 text-slate-600" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
          </div>
        </header>

        {/* Patient Info Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 mb-8 flex flex-wrap gap-8 items-center">
          <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center">
            <User className="h-10 w-10 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-slate-800">Alexander Thorne</h3>
            <div className="flex gap-4 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Calendar className="h-4 w-4" /> 34 Yrs, Male</span>
              <span className="flex items-center gap-1"><Droplets className="h-4 w-4" /> B+ Positive</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Edit Record</button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">New Prescription</button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard icon={<Heart className="text-red-500" />} label="Heart Rate" value="78 bpm" trend="+2% from avg" color="bg-red-50" />
          <StatCard icon={<Thermometer className="text-orange-500" />} label="Body Temp" value="36.8 °C" trend="Normal" color="bg-orange-50" />
          <StatCard icon={<Activity className="text-emerald-500" />} label="Blood Pressure" value="120/80" trend="Stable" color="bg-emerald-50" />
          <StatCard icon={<Droplets className="text-blue-500" />} label="Glucose" value="95 mg/dL" trend="-5% from last" color="bg-blue-50" />
        </div>

        {/* Chart Section */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-slate-800">Health Trends (Weekly)</h3>
            <select className="text-sm border-none bg-slate-100 rounded-md px-2 py-1 focus:ring-0">
              <option>Heart Rate</option>
              <option>Temperature</option>
            </select>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorHr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Area type="monotone" dataKey="heartRate" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorHr)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ icon, label, value, trend, color }) => (
  <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-start gap-4">
    <div className={`p-3 rounded-xl ${color}`}>
      {icon}
    </div>
    <div>
      <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">{label}</p>
      <p className="text-xl font-bold text-slate-800 mt-1">{value}</p>
      <p className="text-xs text-slate-400 mt-1 font-medium">{trend}</p>
    </div>
  </div>
);

export default EmployeeDashboard;
